/**
 * Wanderlust Jewelry - keeps every fee line tied to its piece.
 * Fee lines are hidden products added next to the piece by assets/wj-fee-lines.js:
 *   image engraving   ENGRAVE-IMAGE (fingerprint / paw print, D-81), ENGRAVE-IMAGE-CUSTOM (D-59)  -> "_Image ref"
 *   text engraving    ENGRAVE-TEXT-CHAR, ENGRAVE-SYMBOL (D-75, D-84)                            -> "_Fee ref"
 *   ring resize       RESIZE-UP-4, RESIZE-DOWN-4 (D-77, D-82)                                    -> "_Fee ref"
 * The piece carries the same ref. After any bag change (and on load), this:
 *   - removes a fee line whose piece is no longer in the bag (or never made it in), so a fee can't be bought alone;
 *   - sets the fee quantity to (pieces with that ref) x units per piece, recomputed from the piece's own "Engraving"
 *     text by assets/wj-fee-calc.js perPiece() (characters or symbols; 1 for image and resize fees). "_Per piece" on
 *     the fee line is only a fallback when the calculator can't load: the customer can edit it.
 * When it changes the bag it reloads the page, which is rare and the simplest way to refresh every cart view.
 */
const FEE_SKUS = new Set([
  'ENGRAVE-IMAGE',
  'ENGRAVE-IMAGE-CUSTOM',
  'ENGRAVE-TEXT-CHAR',
  'ENGRAVE-SYMBOL',
  'RESIZE-UP-4',
  'RESIZE-DOWN-4',
]);
/** Any RESIZE-* SKU counts as a fee (the product held 5 / 6 / 8 mm variants before D-82). */
const isFee = (sku) => !!sku && (FEE_SKUS.has(sku) || sku.startsWith('RESIZE-'));
const REF_KEYS = ['_Fee ref', '_Image ref'];
const root = window.Shopify?.routes?.root || '/';
let running = false;

/** Fee products, the free-resize threshold and ring sizes, from snippets/wj-head.liquid (theme settings). */
function feeConfig() {
  try {
    return JSON.parse(document.getElementById('wj-fee-config')?.textContent || 'null');
  } catch {
    return null;
  }
}

/**
 * Fees each engraved / resized piece should carry, worked out from the piece's own order properties with the same
 * calculator as the product page (assets/wj-fee-calc.js). Returns /cart/add.js items for any that are missing, so
 * removing only the fee line can't give free engraving or resizing (Justin's test, 9 Oct 2026).
 * @param {any} cart
 */
async function missingFees(cart) {
  const config = feeConfig();
  if (!config?.calc) return [];
  const calc = await import(config.calc);
  const order = (config.sizes || []).map((size) => size.uk);
  const variants = config.variants || {};
  const have = new Set();
  for (const item of cart.items) {
    if (!isFee(item.sku)) continue;
    const ref = REF_KEYS.map((key) => item.properties?.[key]).find(Boolean);
    have.add(`${ref}|${item.sku}`);
  }
  const out = [];
  for (const item of cart.items) {
    if (isFee(item.sku)) continue;
    const p = item.properties || {};
    const forName =
      item.variant_title && item.variant_title !== 'Default Title' ? `${item.product_title} (${item.variant_title})` : item.product_title;
    const want = [];
    const feeRef = p['_Fee ref'];
    const type = p['Engraving type'];
    if (feeRef && p.Engraving && (!type || type === 'Words')) {
      for (const line of calc.engravingLines(p.Engraving)) want.push({ sku: line.sku, key: '_Fee ref', ref: feeRef, per: line.quantity });
    }
    if (feeRef && p['Resize from'] && p['Resize to'] && !calc.resizeIsFree(item.final_price ?? item.price, config.freeResizeMin)) {
      const sku = calc.resizeSku(calc.resizeDirection(p['Resize from'], p['Resize to'], order));
      if (sku) want.push({ sku, key: '_Fee ref', ref: feeRef, per: 1 });
    }
    const imageRef = p['_Image ref'];
    if (imageRef && type && type !== 'Words') {
      const custom = type === 'Custom image';
      const sku = custom ? 'ENGRAVE-IMAGE-CUSTOM' : 'ENGRAVE-IMAGE';
      const id = custom ? config.custom : config.image;
      if (id && !have.has(`${imageRef}|${sku}`)) {
        have.add(`${imageRef}|${sku}`);
        out.push({ id, quantity: item.quantity, properties: { For: forName, '_Image ref': imageRef } });
      }
    }
    for (const fee of want) {
      const id = variants[fee.sku];
      if (!id || have.has(`${fee.ref}|${fee.sku}`)) continue;
      have.add(`${fee.ref}|${fee.sku}`);
      const properties = { For: forName, [fee.key]: fee.ref };
      if (fee.sku === 'ENGRAVE-TEXT-CHAR' || fee.sku === 'ENGRAVE-SYMBOL') properties['_Per piece'] = String(fee.per);
      out.push({ id, quantity: fee.per * item.quantity, properties });
    }
  }
  return out;
}

async function check() {
  if (running) return;
  running = true;
  try {
    const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const config = feeConfig();
    const calc = config?.calc ? await import(config.calc) : null;
    /** @type {Map<string, { count: number, properties: Record<string, string> }>} */
    const pieces = new Map();
    for (const item of cart.items) {
      if (isFee(item.sku)) continue;
      for (const key of REF_KEYS) {
        const ref = item.properties?.[key];
        if (!ref) continue;
        const piece = pieces.get(ref) || { count: 0, properties: item.properties };
        piece.count += item.quantity;
        pieces.set(ref, piece);
      }
    }
    const updates = {};
    for (const item of cart.items) {
      if (!isFee(item.sku)) continue;
      const ref = REF_KEYS.map((key) => item.properties?.[key]).find(Boolean);
      const piece = pieces.get(ref);
      // Units per piece come from the piece's own "Engraving" text, not the fee line's editable "_Per piece"
      const perPiece = !piece ? 0 : calc ? calc.perPiece(item.sku, piece.properties) : Number(item.properties?.['_Per piece']) || 1;
      const want = (piece?.count || 0) * perPiece;
      if (item.quantity !== want) updates[item.key] = want;
    }
    const missing = await missingFees(cart);
    if (missing.length) {
      await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: missing }),
      });
      if (!Object.keys(updates).length) {
        window.location.reload();
        return;
      }
    }
    if (!Object.keys(updates).length) return;
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    window.location.reload();
  } catch (error) {
    console.warn('[wj-engrave-fee-guard]', error);
  } finally {
    running = false;
  }
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  // Wait for the theme's own request to finish before reading the bag
  const promise = /** @type {any} */ (event).promise;
  (promise ? promise.catch(() => {}) : Promise.resolve()).then(() => setTimeout(check, 300));
});
check();
